/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  CalendarCell,
  CalendarClickEvent,
  CalendarDropTask,
  CalendarTask,
  CalendarTaskHeaderGroup,
  CalendarTasksGroup,
  CalendarViewMonth,
  CustomDatePipe,
  HourSelectionEvent,
  MonthSelectionEvent,
  Time,
} from '@infodata-group/inf-commons';

import { TranslateService } from '@ngx-translate/core';
import { CalendarView } from '../calendar-view';
import { CalendarHorizontalResourcesComponent } from '../calendar-horizontal-resources/calendar-horizontal-resources.component';
import { DatePipe, NgClass, NgStyle, UpperCasePipe } from '@angular/common';
import { DayRowDirective } from '../calendar-view-day/day-row.directive';
import { Telework } from '../telework';
import { TeleworkService } from '../telework.service';
import { DayNotWorkService } from '../day-not-work.service';
import { RemoveBrPipe } from '../calendar-view-resource/calendar-view-resource.component';

@Component({
  selector: 'infl-calendar-view-month-horizontal',
  templateUrl: './calendar-view-month-horizontal.component.html',
  styleUrls: [
    './calendar-view-month-horizontal.component.scss',
    '../compatibility.scss',
  ],
  standalone: true,
  imports: [
    CalendarHorizontalResourcesComponent,
    NgClass,
    DatePipe,
    UpperCasePipe,
    NgStyle,
    DayRowDirective,
    CustomDatePipe,
    RemoveBrPipe,
  ],
})
export class CalendarViewMonthHorizontalComponent implements CalendarView {
  public date = input<Date>();
  public tasks = input<CalendarTask[]>();
  public rowHeight = input(40);
  public canDrag = input(true);
  public canResize = input(true);
  public canSelect = input(true);
  public enable = input(true);
  public resources = input<CalendarTaskHeaderGroup[]>();
  public resourceTitle = input<string>();
  public dragByOutside = input<CalendarDropTask<any>>();
  public sections = input<number>(24);
  public dayTimeStart = input<Time>();
  public dayTimeEnd = input<Time>();
  public teleworks = input<Telework[]>(null);

  public cellClick = output<CalendarCell>();
  public cellsSelection = output<MonthSelectionEvent>();
  public cellPreselection = output<MonthSelectionEvent>();
  public clickOnSelection = output<MonthSelectionEvent>();
  public dblClickOnSelection = output<MonthSelectionEvent>();
  public taskClick = output<CalendarClickEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public dateChange = output<Date>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<{
    date: Date;
    group: { header: string; group: string };
  }>();
  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output<CalendarCell>();
  public taskHover = output<CalendarTask>();
  public taskContextMenu = output<{
    task: CalendarTask;
    style: any;
  }>();

  @ViewChild('header') header: ElementRef;
  @ViewChild('container') container: ElementRef;

  private readonly teleworkService = inject(TeleworkService);
  public readonly translate = inject(TranslateService);
  public readonly dayNotWorkService = inject(DayNotWorkService);

  public viewTasks = signal<{ task: CalendarTask; style: any }[]>([]);

  public days: CalendarCell[];
  public isSafari: boolean;
  public selectedGroup: CalendarTasksGroup;
  public selection: CalendarCell[] = [];

  private previousTaskHeight: string;
  private resizeTask: {
    border: 'left' | 'right';
    task: CalendarTask;
    element: any;
  };
  private baseWidth: number;
  private baseLeft: number;
  private resizeStart: number;
  private timer: any;
  private selectionInterval: { start: CalendarCell; end: CalendarCell } = {
    start: null,
    end: null,
  };
  private onSelect: boolean;
  private draggingTask: { task: CalendarTask; style: any };
  private dragShift: number;
  private saveSelection: {
    cells: CalendarCell[];
    resource: CalendarTasksGroup;
  };
  private lastCellDraggedOver: DayRowDirective;

  constructor() {
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    effect(() => {
      if (this.date()) {
        this.buildMonth();
      }
    });
    effect(() => {
      if (this.resources() || this.tasks()) {
        if (this.header) {
          this.generateTasks();
        } else {
          this.loadTaskAfterView();
        }
        this.clearLastCellHighlighted();
      }
    });
  }

  onMouseMove(event: MouseEvent) {
    if (this.resizeTask) {
      if (this.resizeTask.border === 'right') {
        this.resizeTask.element.style.width = `${
          this.baseWidth + (event.x - this.resizeStart)
        }px`;
      } else {
        this.resizeTask.element.style.left = `${
          this.baseLeft + (event.x - this.resizeStart)
        }px`;
        this.resizeTask.element.style.width = `${
          this.baseWidth - (event.x - this.resizeStart)
        }px`;
      }
    }
  }

  startResize(
    event: MouseEvent,
    task: CalendarTask,
    direction: 'left' | 'right',
  ) {
    if (this.canResize()) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
      this.resizeStart = event.x;
      const target = (<any>event.target).closest('.task');
      this.previousTaskHeight = target.style.height;
      target.style.height = `${this.rowHeight()}px`;
      // target.style.top = `${Math.floor((target.offsetTop - 43) / this.rowHeight) * this.rowHeight + 43}px`;
      target.style.opacity = '0.3';
      this.resizeTask = { element: target, task: task, border: direction };
      this.baseWidth = target.clientWidth;
      this.baseLeft = target.offsetLeft;
    }
  }

  @HostListener('document:mouseup') stopResize() {
    if (this.resizeTask) {
      this.resizeTask.element.style.opacity = '1';
      this.resizeTask.element.style.height = this.previousTaskHeight;
      const width = this.resizeTask.element.clientWidth;
      const pixelPerDay = this.getPixelPerDay();
      const duration = Math.ceil(width / pixelPerDay) - 1;
      const task = this.resizeTask.task;
      if (this.resizeTask.border === 'right') {
        task.dateEnd = task.dateStart.clone().add(duration, 'd');
      } else {
        task.dateStart = task.dateEnd.clone().subtract(duration, 'd');
      }
      this.taskUpdate.emit(task);
      setTimeout(() => {
        this.resizeTask = null;
      }, 100);
      this.generateTasks();
    }
  }

  private buildMonth() {
    const util = new CalendarViewMonth(this.date(), this.translate);
    this.days = util.generateCells();
    this.checkDaysOff();
  }

  private checkDaysOff() {
    for (const day of this.days) {
      day.isOff = !this.dayNotWorkService.isDayWork(day.day.toDate());
      if (day.isOff) {
        day.backgroundColor = this.dayNotWorkService.dayNotWorkColor;
      }
    }
  }

  public generateTasks() {
    if (this.header) {
      setTimeout(() => {
        this.viewTasks.set([]);
        this.generateTasksContent();
      }, 100);
    }
  }

  private generateTasksContent() {
    let top = 69;
    for (const header of this.resources()) {
      top += 40;
      if (header.isOpened) {
        for (const group of header.tasksGroups) {
          const rows = this.createRows(group.tasks);
          group.height = 30;
          for (let i = 0; i < rows.length; ++i) {
            for (const task of rows[i]) {
              const style = this.createStyle(task, top);
              style['top.px'] = top + 30 * i;
              this.viewTasks().push({ task: task, style: style });
            }
          }
          group.height = 40 + (rows.length > 0 ? rows.length - 1 : 0) * 30;
          top += group.height;
        }
      }
    }
  }

  private createStyle(task: CalendarTask, top: number): any {
    let offsetLeft = this.getWidth(0, task.dateStart.get('date') - 1);
    if (offsetLeft < 0) {
      offsetLeft = 0;
    }
    const duration = task.dateEnd.get('date') - task.dateStart.get('date') + 1;
    const width = this.getWidth(task.dateStart.get('date'), duration);
    return {
      'top.px': top,
      'left.px': offsetLeft,
      'width.px': width,
      borderColor: task.borderColor ?? task.backgroundColor,
      color: task.color,
    };
  }

  private getPixelPerDay() {
    return this.header.nativeElement.cells[0].clientWidth + 1;
  }

  private createRows(tasks: CalendarTask[]): CalendarTask[][] {
    const rows: CalendarTask[][] = [];
    for (const task of tasks) {
      const startB = task.dateStart.isBetween(
        this.days[0].day,
        this.days[this.days.length - 1].day,
        'd',
        '[]',
      );
      const endB = task.dateEnd.isBetween(
        this.days[0].day,
        this.days[this.days.length - 1].day,
        'd',
        '[]',
      );
      const startEnd =
        task.dateStart.isBefore(this.days[0].day, 'd') &&
        task.dateEnd.isAfter(this.days[this.days.length - 1].day, 'd');
      if (startB || endB || startEnd) {
        this.insertTask(rows, task);
      }
    }
    return rows;
  }

  private insertTask(rows: CalendarTask[][], insert: CalendarTask) {
    for (let i = 0; i < rows.length; ++i) {
      const row = rows[i];
      let conflict = false;
      for (const task of row) {
        const x1 = task.dateStart.date();
        const y1 = task.dateEnd.date();
        const x2 = insert.dateStart.date();
        const y2 = insert.dateEnd.date();
        const firstCondition = (x1 >= x2 && x1 < y2) || (x1 <= x2 && y1 >= x2);
        const secondCondition = (y1 >= x2 && y1 < y2) || (y1 <= x2 && x1 >= x2);
        if (firstCondition || secondCondition) {
          conflict = true;
          break;
        }
      }
      if (!conflict) {
        row.push(insert);
        return;
      }
    }
    rows.push([insert]);
  }

  private loadTaskAfterView() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      if (this.header) {
        this.generateTasks();
      } else {
        this.loadTaskAfterView();
      }
    }, 400);
  }

  onTaskClick(event: MouseEvent, task: CalendarTask, dblClick?: boolean) {
    if (this.resizeTask) {
      return;
    }
    if (dblClick) {
      this.taskDblClick.emit(new CalendarClickEvent(event, task));
    } else {
      this.taskClick.emit(new CalendarClickEvent(event, task));
    }
  }

  onDragStart(task: { task: CalendarTask; style: any }, event: DragEvent) {
    if (this.canDrag()) {
      this.disablePointerEvent(task);
      this.draggingTask = task;
      this.dragShift =
        event.clientX - (<any>event.target).getBoundingClientRect().left;
      this.dragTask.emit({
        task: this.draggingTask.task,
        shift: this.dragShift,
      });
    }
  }

  allowDrop(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent, day: number, group: string, resource: string) {
    let elements: Element[];
    this.clearLastCellHighlighted();
    this.enablePointerEvent();
    if (this.draggingTask) {
      elements = document.elementsFromPoint(event.x - this.dragShift, event.y);
    } else {
      elements = document.elementsFromPoint(event.x, event.y);
    }
    for (const elem of elements) {
      const cell = this.days.find((d) => d.day.get('date') === day);
      if (cell) {
        this.onDropTask(cell, group, resource);
      }
    }
  }

  onDropTask(cell: CalendarCell, group: string, resource: string) {
    if (this.canDrag()) {
      let task;
      if (this.draggingTask) {
        task = this.draggingTask.task;
        let m = task.dateStart.clone();
        m = m
          .set('year', cell.day.year())
          .set('month', cell.day.month())
          .set('date', cell.day.date());
        task.updateDateStart(m);
        task.group = { header: group, group: resource };
        this.taskUpdate.emit(task);
        this.draggingTask = null;
        this.generateTasks();
      }

      if (this.dragByOutside()) {
        const date = cell.day.toDate();
        const r = { header: group, group: resource };
        this.taskCreation.emit({ date: date, group: r });
      }
    }
  }

  public startSelection(
    event: MouseEvent,
    day: CalendarCell,
    resource: CalendarTasksGroup,
  ) {
    if (this.canSelect()) {
      if (!day.selected || this.selectedGroup !== resource) {
        this.selectedGroup = resource;
        event.stopPropagation();
        event.preventDefault();
        for (const section of this.selection) {
          section.selected = false;
        }
        this.selection = [day];
        day.selected = true;
        this.onSelect = true;
        this.selectionInterval.start = day;
      } else if (event.which === 3 && day.selected) {
        this.clickOnSelection.emit(
          this.getMonthSelectionEvent(
            this.saveSelection.cells,
            event,
            this.saveSelection.resource,
          ),
        );
      }
    }
  }

  private getMonthSelectionEvent(
    cells: CalendarCell[],
    event: MouseEvent,
    resource: CalendarTasksGroup,
  ): MonthSelectionEvent {
    const hourSelection = new HourSelectionEvent(
      {
        start: this.dayTimeStart(),
        end: this.dayTimeEnd(),
        resource: resource,
      },
      event,
    );
    return new MonthSelectionEvent(cells, hourSelection);
  }

  overZone(event: MouseEvent, day: CalendarCell, resource: CalendarTasksGroup) {
    if (this.onSelect) {
      for (const section of this.selection) {
        section.selected = false;
      }
      this.selection = [];
      this.selectionInterval.end = day;

      for (const d of this.days) {
        const x = d.day.get('date');
        let start = this.selectionInterval.start.day.get('date');
        let end = this.selectionInterval.end.day.get('date');
        if (start > end) {
          const temp = start;
          start = end;
          end = temp;
        }
        if (x >= start && x <= end) {
          this.selection.push(d);
          d.selected = true;
        }
      }
    } else {
      this.cellPreselection.emit(
        this.getMonthSelectionEvent([day], event, resource),
      );
    }
  }

  public stopSelection(event: MouseEvent) {
    this.onSelect = false;
    if (this.selectionInterval.start) {
      if (!this.selectionInterval.end) {
        this.selectionInterval.end = this.selectionInterval.start;
      }
      this.saveSelection = {
        cells: this.selection,
        resource: this.selectedGroup,
      };
      this.cellsSelection.emit(
        this.getMonthSelectionEvent(this.selection, event, this.selectedGroup),
      );
    }
    this.selectionInterval = { start: null, end: null };
  }

  deselect(): void {
    for (const section of this.selection) {
      section.selected = false;
    }
    this.selection = [];
  }

  private getWidth(start: number, length: number) {
    let width = 0;
    const cells = this.header.nativeElement.cells;
    for (let i = start; i < start + length && i < cells.length; ++i) {
      width += cells[i].getBoundingClientRect().width;
    }

    if (start === cells.length) {
      width += cells[start - 1].getBoundingClientRect().width;
    }
    return width;
  }

  onClickOnSelection(
    cells: CalendarCell[],
    event: MouseEvent,
    resource: CalendarTasksGroup,
    dblClick?: boolean,
  ) {
    if (dblClick) {
      this.dblClickOnSelection.emit(
        this.getMonthSelectionEvent(cells, event, resource),
      );
      return;
    }
    this.clickOnSelection.emit(
      this.getMonthSelectionEvent(cells, event, resource),
    );
  }

  /**
   * disable pointerEvent to catch drop event on zone, not on task
   * @param task
   * @private
   */
  private disablePointerEvent(task: { task: CalendarTask; style: any }) {
    this.viewTasks().forEach((t) => {
      if (t.task.id !== task.task.id) {
        t.style['pointer-events'] = 'none';
      } else {
        // todo to improve when we have time
        setTimeout(() => {
          task.style['pointer-events'] = 'none';
        }, 1);
      }
    });
  }

  /**
   * enable pointerEvent to catch drop event on zone, not on task
   * @private
   */
  private enablePointerEvent() {
    this.viewTasks().forEach((t) => {
      t.style['pointer-events'] = 'auto';
    });
  }

  public findTask(id: string): { style: any; task: CalendarTask } {
    const t = this.viewTasks().find((vt) => vt.task.id === id);
    this.container.nativeElement.scrollTo({
      top: t.style['top.px'],
      left: t.style['left.px'],
      behavior: 'smooth',
    });
    return undefined;
  }

  getScrollPosition(): { x: number; y: number } {
    if (this.container) {
      return {
        x: this.container.nativeElement.scrollLeft,
        y: this.container.nativeElement.scrollTop,
      };
    } else {
      return null;
    }
  }

  setScrollPosition(x: number, y: number) {
    if (this.container.nativeElement) {
      this.container.nativeElement.scrollLeft = x;
      this.container.nativeElement.scrollTop = y;
    }
  }

  public scrollToDate(date: Date): void {}

  private clearLastCellHighlighted() {
    if (this.lastCellDraggedOver) {
      this.lastCellDraggedOver.isHovered = false;
      this.lastCellDraggedOver = null;
    }
  }

  protected onDragLeaveTbody() {
    this.clearLastCellHighlighted();
  }

  protected onDragOver(event: DragEvent, cell: DayRowDirective) {
    event.preventDefault();
    this.clearLastCellHighlighted();
    this.lastCellDraggedOver = cell;
    cell.isHovered = true;
  }
}
